import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, setDoc, getDocs, collection, updateDoc } from "firebase/firestore";
import app from "../../firebase";
import {
  formatCalendarDate,
  getCalendarRangeStatus,
  isCalendarDateInRange,
  useCalendarClock,
  validateCalendarDatesNotInPast,
  validateCalendarDateRanges,
  validateConsecutiveSchoolYear
} from "../../utils/announcementDates";

export default function AnnouncementControl() {
  const todayKey = useCalendarClock();
  const [announcement, setAnnouncement] = useState("");
  
  // 1st Semester - School Year
  const [firstSemYearStart, setFirstSemYearStart] = useState("");
  const [firstSemYearEnd, setFirstSemYearEnd] = useState("");
  
  // 1st Semester - Semester Dates
  const [firstSemStart, setFirstSemStart] = useState("");
  const [firstSemEnd, setFirstSemEnd] = useState("");
  
  // 1st Semester - Enrollment Window
  const [firstSemEnrollmentStart, setFirstSemEnrollmentStart] = useState("");
  const [firstSemEnrollmentEnd, setFirstSemEnrollmentEnd] = useState("");
  
  // 1st Semester - Appointment Window (for New Students only)
  const [firstSemAppointmentStart, setFirstSemAppointmentStart] = useState("");
  const [firstSemAppointmentEnd, setFirstSemAppointmentEnd] = useState("");
  
  // 2nd Semester - School Year
  const [secondSemYearStart, setSecondSemYearStart] = useState("");
  const [secondSemYearEnd, setSecondSemYearEnd] = useState("");
  
  // 2nd Semester - Semester Dates
  const [secondSemStart, setSecondSemStart] = useState("");
  const [secondSemEnd, setSecondSemEnd] = useState("");
  
  // 2nd Semester - Enrollment Window
  const [secondSemEnrollmentStart, setSecondSemEnrollmentStart] = useState("");
  const [secondSemEnrollmentEnd, setSecondSemEnrollmentEnd] = useState("");
  
  // 2nd Semester - Appointment Window (for New Students only)
  const [secondSemAppointmentStart, setSecondSemAppointmentStart] = useState("");
  const [secondSemAppointmentEnd, setSecondSemAppointmentEnd] = useState("");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const getNextSchoolYear = (start, end) => {
    const startNum = parseInt(start, 10);
    const endNum = parseInt(end, 10);
    if (Number.isNaN(startNum) || Number.isNaN(endNum)) return { nextStart: "", nextEnd: "" };
    return { nextStart: `${startNum + 1}`, nextEnd: `${endNum + 1}` };
  };

  const getSemesterStatus = (startDate, endDate) => {
    return getCalendarRangeStatus(startDate, endDate, todayKey);
  };

  const getStatusStyle = (status) => {
    if (status === "Ongoing") return { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" };
    if (status === "Ended") return { background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a" };
    return { background: "#fff8e1", color: "#b26a00", border: "1px solid #ffe0b2" };
  };

  const isEnrollmentWindowOpen = (startDate, endDate) => {
    return isCalendarDateInRange(startDate, endDate, todayKey);
  };

  const getOpenClosedStatus = (isOpen) => {
    return isOpen ? "OPEN" : "CLOSED";
  };

  const getOpenClosedStyle = (isOpen) => {
    return isOpen
      ? { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }
      : { background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a" };
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    return formatCalendarDate(dateStr) || "—";
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      const db = getFirestore(app);
      const ref = doc(db, "systemSettings", "announcement");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setAnnouncement(data.announcement || "");
        
        // 1st Semester
        setFirstSemYearStart(data.firstSemYearStart || "");
        setFirstSemYearEnd(data.firstSemYearEnd || "");
        setFirstSemStart(data.firstSemStart || "");
        setFirstSemEnd(data.firstSemEnd || "");
        setFirstSemEnrollmentStart(data.firstSemEnrollmentStart || "");
        setFirstSemEnrollmentEnd(data.firstSemEnrollmentEnd || "");
        setFirstSemAppointmentStart(data.firstSemAppointmentStart || "");
        setFirstSemAppointmentEnd(data.firstSemAppointmentEnd || "");
        
        // 2nd Semester
        setSecondSemYearStart(data.secondSemYearStart || "");
        setSecondSemYearEnd(data.secondSemYearEnd || "");
        setSecondSemStart(data.secondSemStart || "");
        setSecondSemEnd(data.secondSemEnd || "");
        setSecondSemEnrollmentStart(data.secondSemEnrollmentStart || "");
        setSecondSemEnrollmentEnd(data.secondSemEnrollmentEnd || "");
        setSecondSemAppointmentStart(data.secondSemAppointmentStart || "");
        setSecondSemAppointmentEnd(data.secondSemAppointmentEnd || "");
      }
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    const rangeError = validateCalendarDateRanges([
      { label: "1st Semester dates", start: firstSemStart, end: firstSemEnd },
      { label: "1st Semester enrollment window", start: firstSemEnrollmentStart, end: firstSemEnrollmentEnd },
      { label: "1st Semester appointment window", start: firstSemAppointmentStart, end: firstSemAppointmentEnd },
      { label: "2nd Semester dates", start: secondSemStart, end: secondSemEnd },
      { label: "2nd Semester enrollment window", start: secondSemEnrollmentStart, end: secondSemEnrollmentEnd },
      { label: "2nd Semester appointment window", start: secondSemAppointmentStart, end: secondSemAppointmentEnd }
    ]);
    const pastDateError = validateCalendarDatesNotInPast([
      { label: "1st Semester start date", value: firstSemStart },
      { label: "1st Semester end date", value: firstSemEnd },
      { label: "1st Semester enrollment start date", value: firstSemEnrollmentStart },
      { label: "1st Semester enrollment end date", value: firstSemEnrollmentEnd },
      { label: "1st Semester appointment start date", value: firstSemAppointmentStart },
      { label: "1st Semester appointment end date", value: firstSemAppointmentEnd },
      { label: "2nd Semester start date", value: secondSemStart },
      { label: "2nd Semester end date", value: secondSemEnd },
      { label: "2nd Semester enrollment start date", value: secondSemEnrollmentStart },
      { label: "2nd Semester enrollment end date", value: secondSemEnrollmentEnd },
      { label: "2nd Semester appointment start date", value: secondSemAppointmentStart },
      { label: "2nd Semester appointment end date", value: secondSemAppointmentEnd },
    ], todayKey);
    const yearError = validateConsecutiveSchoolYear("1st Semester school year", firstSemYearStart, firstSemYearEnd) ||
      validateConsecutiveSchoolYear("2nd Semester school year", secondSemYearStart, secondSemYearEnd);
    if (rangeError || pastDateError || yearError) {
      setMessage(rangeError || pastDateError || yearError);
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const db = getFirestore(app);
      const ref = doc(db, "systemSettings", "announcement");
      
      // Compute semester and window statuses for 1st Semester
      const firstSemStatus = getSemesterStatus(firstSemStart, firstSemEnd);
      const firstSemEnrollmentStatus = getOpenClosedStatus(isEnrollmentWindowOpen(firstSemEnrollmentStart, firstSemEnrollmentEnd));
      const firstSemAppointmentStatus = getOpenClosedStatus(isEnrollmentWindowOpen(firstSemAppointmentStart, firstSemAppointmentEnd));
      
      // Compute semester and window statuses for 2nd Semester
      const secondSemStatus = getSemesterStatus(secondSemStart, secondSemEnd);
      const secondSemEnrollmentStatus = getOpenClosedStatus(isEnrollmentWindowOpen(secondSemEnrollmentStart, secondSemEnrollmentEnd));
      const secondSemAppointmentStatus = getOpenClosedStatus(isEnrollmentWindowOpen(secondSemAppointmentStart, secondSemAppointmentEnd));
      
      await setDoc(ref, {
        announcement,
        
        // 1st Semester
        firstSemYearStart: firstSemYearStart || null,
        firstSemYearEnd: firstSemYearEnd || null,
        firstSemStart: firstSemStart || null,
        firstSemEnd: firstSemEnd || null,
        firstSemEnrollmentStart: firstSemEnrollmentStart || null,
        firstSemEnrollmentEnd: firstSemEnrollmentEnd || null,
        firstSemAppointmentStart: firstSemAppointmentStart || null,
        firstSemAppointmentEnd: firstSemAppointmentEnd || null,
        firstSemStatus,
        firstSemEnrollmentStatus,
        firstSemAppointmentStatus,
        
        // 2nd Semester
        secondSemYearStart: secondSemYearStart || null,
        secondSemYearEnd: secondSemYearEnd || null,
        secondSemStart: secondSemStart || null,
        secondSemEnd: secondSemEnd || null,
        secondSemEnrollmentStart: secondSemEnrollmentStart || null,
        secondSemEnrollmentEnd: secondSemEnrollmentEnd || null,
        secondSemAppointmentStart: secondSemAppointmentStart || null,
        secondSemAppointmentEnd: secondSemAppointmentEnd || null,
        secondSemStatus,
        secondSemEnrollmentStatus,
        secondSemAppointmentStatus,
      }, { merge: true });

      setMessage("Settings updated successfully.");
    } catch (err) {
      setMessage("Failed to update settings.");
    }
    setSaving(false);
  };

  if (loading) return <div>Loading announcement settings...</div>;

  // Generate year options (2025 to 2029)
  const yearOptions = Array.from({ length: 11 }, (_, i) => `${2025 + i}`);

  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 6px 18px rgba(0,0,0,0.08)', padding: 40, maxWidth: 1100, margin: '32px auto' }}>
      <h2 style={{ color: '#155c2b', fontWeight: 800, marginBottom: 8 }}>System Announcement & Enrollment Control</h2>
      <div style={{ color: "#6b7280", marginBottom: 20, fontSize: 14 }}>
        Configure admission windows for new students and semester windows for enrolled students.
      </div>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontWeight: 700 }}>Announcement Message:</label>
          <textarea value={announcement} onChange={e => setAnnouncement(e.target.value)} rows={4} style={{ width: '100%', fontSize: 16, borderRadius: 8, border: '1px solid #ddd', marginTop: 6, padding: 12 }} />
        </div>

        {/* ENROLLMENT CONTROL CARD - For both Enrolled and New Students */}
        <div style={{ background: "#f8fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 20, marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#155c2b", marginBottom: 4 }}>Enrollment Control</div>
          <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
            Set school year, semester windows, enrollment windows, and appointment windows for each semester.
          </div>

          {/* 1st Sem */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>1st Semester</div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 600, marginRight: 12 }}>School Year:</label>
              <select
                value={firstSemYearStart}
                onChange={e => {
                  const nextStart = e.target.value;
                  setFirstSemYearStart(nextStart);
                  const nextEnd = String(Number(nextStart) + 1);
                  setFirstSemYearEnd(nextStart && Number(nextEnd) <= 2035 ? nextEnd : "");
                }}
                style={{ fontSize: 16, borderRadius: 8, border: '1px solid #ccc', marginRight: 8, padding: 6 }}
              >
                <option value="">Start Year</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <span style={{ margin: "0 8px" }}>-</span>
              <select
                value={firstSemYearEnd}
                onChange={e => setFirstSemYearEnd(e.target.value)}
                style={{ fontSize: 16, borderRadius: 8, border: '1px solid #ccc', padding: 6 }}
                disabled={!firstSemYearStart}
              >
                <option value="">End Year</option>
                {yearOptions
                  .filter(y => firstSemYearStart && Number(y) === Number(firstSemYearStart) + 1)
                  .map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button
                type="button"
                onClick={() => {
                  const { nextStart, nextEnd } = getNextSchoolYear(firstSemYearStart, firstSemYearEnd);
                  setFirstSemYearStart(nextStart);
                  setFirstSemYearEnd(nextEnd);
                }}
                disabled={!firstSemYearStart || !firstSemYearEnd || Number(firstSemYearStart) >= 2034}
                style={{ marginLeft: 12, padding: "6px 12px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: !firstSemYearStart || !firstSemYearEnd || Number(firstSemYearStart) >= 2034 ? "not-allowed" : "pointer", fontWeight: 600 }}
              >
                New
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Semester Dates</div>
              <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, fontWeight: 700, ...getStatusStyle(getSemesterStatus(firstSemStart, firstSemEnd)) }}>
                {getSemesterStatus(firstSemStart, firstSemEnd)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: "wrap", marginBottom: 10 }}>
              <input type="date" min={todayKey} value={firstSemStart ? firstSemStart.split('T')[0] : ''} onChange={e => setFirstSemStart(e.target.value)} />
              <span>to</span>
              <input type="date" min={todayKey} value={firstSemEnd ? firstSemEnd.split('T')[0] : ''} onChange={e => setFirstSemEnd(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              {formatDisplayDate(firstSemStart)} to {formatDisplayDate(firstSemEnd)}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Enrollment Window</div>
              <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, fontWeight: 700, ...getOpenClosedStyle(isEnrollmentWindowOpen(firstSemEnrollmentStart, firstSemEnrollmentEnd)) }}>
                {isEnrollmentWindowOpen(firstSemEnrollmentStart, firstSemEnrollmentEnd) ? "OPEN" : "CLOSED"}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: "wrap" }}>
              <input type="date" min={todayKey} value={firstSemEnrollmentStart ? firstSemEnrollmentStart.split('T')[0] : ''} onChange={e => setFirstSemEnrollmentStart(e.target.value)} />
              <span>to</span>
              <input type="date" min={todayKey} value={firstSemEnrollmentEnd ? firstSemEnrollmentEnd.split('T')[0] : ''} onChange={e => setFirstSemEnrollmentEnd(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6, marginBottom: 12 }}>
              {formatDisplayDate(firstSemEnrollmentStart)} to {formatDisplayDate(firstSemEnrollmentEnd)}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Appointment Window <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}>(for New Students only)</span></div>
              <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, fontWeight: 700, ...getOpenClosedStyle(isEnrollmentWindowOpen(firstSemAppointmentStart, firstSemAppointmentEnd)) }}>
                {isEnrollmentWindowOpen(firstSemAppointmentStart, firstSemAppointmentEnd) ? "OPEN" : "CLOSED"}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: "wrap" }}>
              <input type="date" min={todayKey} value={firstSemAppointmentStart ? firstSemAppointmentStart.split('T')[0] : ''} onChange={e => setFirstSemAppointmentStart(e.target.value)} />
              <span>to</span>
              <input type="date" min={todayKey} value={firstSemAppointmentEnd ? firstSemAppointmentEnd.split('T')[0] : ''} onChange={e => setFirstSemAppointmentEnd(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              {formatDisplayDate(firstSemAppointmentStart)} to {formatDisplayDate(firstSemAppointmentEnd)}
            </div>
          </div>

          <div style={{ height: 1, background: "#e5e7eb", margin: "16px 0 20px" }} />

          {/* 2nd Sem */}
          <div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>2nd Semester</div>
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontWeight: 600, marginRight: 12 }}>School Year:</label>
              <select
                value={secondSemYearStart}
                onChange={e => {
                  const nextStart = e.target.value;
                  setSecondSemYearStart(nextStart);
                  const nextEnd = String(Number(nextStart) + 1);
                  setSecondSemYearEnd(nextStart && Number(nextEnd) <= 2035 ? nextEnd : "");
                }}
                style={{ fontSize: 16, borderRadius: 8, border: '1px solid #ccc', marginRight: 8, padding: 6 }}
              >
                <option value="">Start Year</option>
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <span style={{ margin: "0 8px" }}>-</span>
              <select
                value={secondSemYearEnd}
                onChange={e => setSecondSemYearEnd(e.target.value)}
                style={{ fontSize: 16, borderRadius: 8, border: '1px solid #ccc', padding: 6 }}
                disabled={!secondSemYearStart}
              >
                <option value="">End Year</option>
                {yearOptions
                  .filter(y => secondSemYearStart && Number(y) === Number(secondSemYearStart) + 1)
                  .map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button
                type="button"
                onClick={() => {
                  const { nextStart, nextEnd } = getNextSchoolYear(secondSemYearStart, secondSemYearEnd);
                  setSecondSemYearStart(nextStart);
                  setSecondSemYearEnd(nextEnd);
                }}
                disabled={!secondSemYearStart || !secondSemYearEnd || Number(secondSemYearStart) >= 2034}
                style={{ marginLeft: 12, padding: "6px 12px", borderRadius: 6, border: "1px solid #ccc", background: "#fff", cursor: !secondSemYearStart || !secondSemYearEnd || Number(secondSemYearStart) >= 2034 ? "not-allowed" : "pointer", fontWeight: 600 }}
              >
                New
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Semester Dates</div>
              <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, fontWeight: 700, ...getStatusStyle(getSemesterStatus(secondSemStart, secondSemEnd)) }}>
                {getSemesterStatus(secondSemStart, secondSemEnd)}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: "wrap", marginBottom: 10 }}>
              <input type="date" min={todayKey} value={secondSemStart ? secondSemStart.split('T')[0] : ''} onChange={e => setSecondSemStart(e.target.value)} />
              <span>to</span>
              <input type="date" min={todayKey} value={secondSemEnd ? secondSemEnd.split('T')[0] : ''} onChange={e => setSecondSemEnd(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              {formatDisplayDate(secondSemStart)} to {formatDisplayDate(secondSemEnd)}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Enrollment Window</div>
              <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, fontWeight: 700, ...getOpenClosedStyle(isEnrollmentWindowOpen(secondSemEnrollmentStart, secondSemEnrollmentEnd)) }}>
                {isEnrollmentWindowOpen(secondSemEnrollmentStart, secondSemEnrollmentEnd) ? "OPEN" : "CLOSED"}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: "wrap" }}>
              <input type="date" min={todayKey} value={secondSemEnrollmentStart ? secondSemEnrollmentStart.split('T')[0] : ''} onChange={e => setSecondSemEnrollmentStart(e.target.value)} />
              <span>to</span>
              <input type="date" min={todayKey} value={secondSemEnrollmentEnd ? secondSemEnrollmentEnd.split('T')[0] : ''} onChange={e => setSecondSemEnrollmentEnd(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6, marginBottom: 12 }}>
              {formatDisplayDate(secondSemEnrollmentStart)} to {formatDisplayDate(secondSemEnrollmentEnd)}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>Appointment Window <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 400 }}>(for New Students only)</span></div>
              <span style={{ fontSize: 12, padding: "2px 8px", borderRadius: 999, fontWeight: 700, ...getOpenClosedStyle(isEnrollmentWindowOpen(secondSemAppointmentStart, secondSemAppointmentEnd)) }}>
                {isEnrollmentWindowOpen(secondSemAppointmentStart, secondSemAppointmentEnd) ? "OPEN" : "CLOSED"}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: "wrap" }}>
              <input type="date" min={todayKey} value={secondSemAppointmentStart ? secondSemAppointmentStart.split('T')[0] : ''} onChange={e => setSecondSemAppointmentStart(e.target.value)} />
              <span>to</span>
              <input type="date" min={todayKey} value={secondSemAppointmentEnd ? secondSemAppointmentEnd.split('T')[0] : ''} onChange={e => setSecondSemAppointmentEnd(e.target.value)} />
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              {formatDisplayDate(secondSemAppointmentStart)} to {formatDisplayDate(secondSemAppointmentEnd)}
            </div>
          </div>
        </div>

        <button type="submit" disabled={saving} style={{ background: '#18803b', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontWeight: 700, fontSize: 16, letterSpacing: 0.5, cursor: 'pointer' }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {message && <div style={{ marginTop: 16, color: message.includes('success') ? '#18803b' : '#b71c1c', fontWeight: 600 }}>{message}</div>}
      </form>
    </div>
  );
}